import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Copy, KeyRound, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TeachersPanel } from "@/components/teacher/TeachersPanel";
import { ThemesPanel } from "@/components/teacher/Social";
import { FeedbackPanel } from "@/components/teacher/SiteAdmin";

export default function CodesPage() {
  const { teacher } = useAuth();
  const [codes, setCodes] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const load = () => api.get("/auth/codes").then((r) => setCodes(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (teacher?.role === "admin") load(); }, [teacher]);
  if (teacher?.role !== "admin") return <Navigate to="/ogretmen/havuzlar" replace />;

  const create = async (academic = false) => {
    try {
      const { data } = await api.post("/auth/codes", { academic });
      navigator.clipboard?.writeText(data.code);
      toast.success(`${academic ? "Akademisyen kodu" : "Kod"} oluşturuldu ve kopyalandı: ${data.code}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (code) => {
    try { await api.delete(`/auth/codes/${code}`); toast.success("Kod silindi"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const archive = async (code, archived) => {
    try { await api.patch(`/auth/codes/${code}`, { archived }); toast.success(archived ? "Arşivlendi" : "Arşivden çıkarıldı"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const dev = Boolean(teacher?.developer);
  const archivedCount = codes.filter((c) => c.archived).length;
  const visible = codes.filter((c) => !!c.archived === showArchived);

  return (
    <div className="space-y-6" data-testid="codes-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Yönetim: kodlar ve öğretmenler</h1>
          <p className="text-slate-400 mt-1">Her kod tek kullanımlıktır. Öğretmen bu kodla kendi e-postası ve şifresiyle doğrudan kayıt olur.{!dev && " Kod oluşturma ve hesap düzenleme yalnızca geliştiriciye açık; siz görüntüleyebilirsiniz."}</p>
        </div>
        {dev && <Button onClick={() => create(false)} data-testid="create-code-button"><KeyRound className="h-4 w-4 mr-2" />Yeni kod oluştur</Button>}
        {dev && <Button variant="outline" onClick={() => create(true)} data-testid="create-academic-code-button"><KeyRound className="h-4 w-4 mr-2" />Akademisyen kodu</Button>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant={showArchived ? "ghost" : "secondary"} onClick={() => setShowArchived(false)} data-testid="codes-tab-active">Aktif ({codes.length - archivedCount})</Button>
        <Button size="sm" variant={showArchived ? "secondary" : "ghost"} onClick={() => setShowArchived(true)} data-testid="codes-tab-archived">Arşiv ({archivedCount})</Button>
      </div>
      <div className="space-y-3">
        {visible.length === 0 && <p className="text-slate-400" data-testid="codes-empty">{showArchived ? "Arşivde kod yok." : "Henüz kod oluşturulmadı."}</p>}
        {visible.map((c) => (
          <div key={c.code} className="panel p-4 flex flex-wrap items-center gap-4" data-testid={`code-row-${c.code}`}>
            <span className="font-display text-2xl font-bold tracking-widest text-[#FACC15]">{c.code}</span>
            {c.academic && <span className="text-xs rounded-full bg-sky-500/20 text-sky-300 px-2 py-0.5 font-bold" data-testid={`code-academic-${c.code}`}>Akademisyen</span>}
            <span className={`text-sm flex-1 ${c.used_by ? "text-slate-400" : "text-[#4ADE80]"}`}>
              {c.used_by ? `Kullanıldı: ${c.used_by}` : "Kullanılmadı"} · {new Date(c.created_at).toLocaleString("tr-TR")}
            </span>
            {!c.used_by && (
              <Button size="sm" variant="ghost" title="Kopyala" onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Kopyalandı"); }} data-testid={`code-copy-${c.code}`}><Copy className="h-4 w-4" /></Button>
            )}
            {dev && <>
            <Button size="sm" variant="ghost" title={c.archived ? "Arşivden çıkar" : "Arşivle"} onClick={() => archive(c.code, !c.archived)} data-testid={`code-archive-${c.code}`}>
              {c.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="ghost" title="Sil" onClick={() => remove(c.code)} className="text-red-400" data-testid={`code-delete-${c.code}`}><Trash2 className="h-4 w-4" /></Button>
            </>}
          </div>
        ))}
      </div>
      <TeachersPanel />
      <ThemesPanel />
      <FeedbackPanel />
    </div>
  );
}
